import type { ReactNode } from 'react';
import { Box, Text } from '@chakra-ui/react';

type MarkdownBlock =
  | { kind: 'text'; text: string }
  | { kind: 'table'; headers: string[]; rows: string[][] };

/** Bold spans and pipe tables. Everything else stays as text. No HTML. */
export function parsePlanMarkdown(source: string): MarkdownBlock[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: MarkdownBlock[] = [];
  let paragraph: string[] = [];
  const flush = () => {
    const text = paragraph.join('\n').trim();
    if (text) blocks.push({ kind: 'text', text });
    paragraph = [];
  };
  let index = 0;
  while (index < lines.length) {
    if (isTableRow(lines[index])) {
      flush();
      const tableLines: string[] = [];
      while (index < lines.length && isTableRow(lines[index])) {
        tableLines.push(lines[index]);
        index += 1;
      }
      const table = tableBlock(tableLines);
      if (table) blocks.push(table);
      else blocks.push({ kind: 'text', text: tableLines.join('\n') });
      continue;
    }
    if (!lines[index].trim()) flush();
    else paragraph.push(lines[index]);
    index += 1;
  }
  flush();
  return blocks;
}

type SafeMarkdownProps = {
  text: string;
  testId?: string;
  tableTestId?: string;
  fontSize?: string;
  color?: string;
  mt?: string;
};

/** Renders bold and aligned pipe tables as text nodes. Markup is never parsed as HTML. */
export function SafeMarkdown({
  text,
  testId = 'safe-markdown',
  tableTestId = 'safe-markdown-table',
  fontSize = '14px',
  color,
  mt
}: SafeMarkdownProps) {
  const blocks = parsePlanMarkdown(text);
  return (
    <Box mt={mt} data-testid={testId}>
      {blocks.map((block, index) => block.kind === 'table' ? (
        <Box
          key={index}
          as="table"
          w="100%"
          fontSize={fontSize}
          mt="8px"
          data-testid={tableTestId}
          sx={{ borderCollapse: 'collapse' }}
        >
          <thead>
            <tr>
              {block.headers.map((cell, cellIndex) => (
                <Text as="th" key={cellIndex} textAlign="left" py="2px" pr="12px" fontWeight="600" color="gray.500">
                  {inlineMarkdown(cell)}
                </Text>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <Text as="td" key={cellIndex} py="2px" pr="12px">{inlineMarkdown(cell)}</Text>
                ))}
              </tr>
            ))}
          </tbody>
        </Box>
      ) : (
        <Text key={index} fontSize={fontSize} color={color} whiteSpace="pre-wrap">{inlineMarkdown(block.text)}</Text>
      ))}
    </Box>
  );
}

function inlineMarkdown(text: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.filter((part) => part !== '').map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length >= 4) {
      return <Text as="strong" key={index} fontWeight="700">{part.slice(2, -2)}</Text>;
    }
    return <Text as="span" key={index}>{part}</Text>;
  });
}

function isTableRow(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.length > 1;
}

function tableBlock(lines: string[]): MarkdownBlock | null {
  const rows = lines.map(splitRow).filter((row) => row.length > 0 && !row.every(isSeparatorCell));
  if (rows.length < 2) return null;
  const width = rows[0].length;
  if (rows.some((row) => row.length !== width)) return null;
  return { kind: 'table', headers: rows[0], rows: rows.slice(1) };
}

function splitRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map((cell) => cell.trim());
}

function isSeparatorCell(cell: string): boolean {
  return /^:?-{3,}:?$/.test(cell);
}
